// MUI app chrome for the "-mui" world (Task 0.1). Gives the MUI pages their own
// top bar + nav rail, independent of the kt AppShell (which is deleted at
// cutover). Reproduces the kt shell's nav model/behavior in MUI components.
import "@fontsource/dm-sans/400.css";
import "@fontsource/dm-sans/500.css";
import "@fontsource/dm-sans/600.css";
import "@fontsource/dm-sans/700.css";
import "@fontsource/dm-mono/400.css";
import "@fontsource/dm-mono/500.css";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  AppBar,
  Box,
  CssBaseline,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { brainTheme, brainTokens } from "../design/brainTheme";
import { SidebarSlotProvider, SidebarSlotOutlet } from "../design/SidebarContext";
import { AgentProvider } from "../agent/AgentProvider";
import { AgentWidget } from "../agent/AgentWidget";
import { CorrespondenceProvider } from "../correspondence/CorrespondenceProvider";
import { RaiseAtStandupDialog } from "../components/RaiseAtStandupDialog";
import { NotificationBell } from "./NotificationBell";
import { NotificationsProvider } from "../lib/NotificationsProvider";
import { useProfile } from "../lib/profile";
import { callTool } from "../lib/api";
import { signOut } from "../lib/auth";
import { Icon } from "../design/ui"; // ponytail: reuse kt Icon (presentational SVG, has all nav glyphs); swap to mui/icons at cutover if desired

// ponytail: nav model duplicated from design/AppShell.tsx on purpose — the kt shell is
// deleted at cutover, so coupling two shells now buys nothing. Keep in sync until then.
type NavItem = { to: string; icon: string; label: string; badge?: boolean; admin?: boolean; finance?: boolean };

// Consolidated nav — canonical URLs only (kt AppShell + -mui suffixes retired).
const NAV: NavItem[] = [
  { to: "/today", icon: "home", label: "Today" },
  { to: "/brain-health", icon: "activity-heartbeat", label: "Brain Health" },
  { to: "/standup", icon: "clipboard-list", label: "Standup", badge: true },
  { to: "/desk", icon: "messages", label: "Desk" },
  { to: "/customers", icon: "building", label: "Customer Knowledge" },
  { to: "/aeronet", icon: "stack-2", label: "Aeronet Knowledge" },
  { to: "/projects", icon: "folder", label: "Projects" },
  { to: "/finance", icon: "report-money", label: "Finance", finance: true },
  { to: "/atlas", icon: "galaxy", label: "Atlas" },
  { to: "/cockpit", icon: "checkup-list", label: "Review Cockpit" },
  { to: "/admin/users", icon: "users", label: "Users", admin: true },
  { to: "/settings", icon: "settings", label: "Settings" },
];
const ADMIN_NAV: NavItem[] = [
  { to: "/admin", icon: "layout-dashboard", label: "Overview" },
  { to: "/admin/integrations", icon: "plug", label: "Integrations" },
  { to: "/admin/audit", icon: "shield-check", label: "Audit" },
];

const c = brainTokens.color;
const L = brainTokens.layout;

/** One nav rail row: icon + label + active teal treatment + optional count badge. */
function NavRow({ item, active, count }: { item: NavItem; active: boolean; count: number }) {
  return (
    <NavLink key={item.to} to={item.to} aria-current={active ? "page" : undefined} style={{ textDecoration: "none" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1,
          mx: 1,
          borderRadius: `${brainTokens.radius.button}px`,
          color: active ? c.tealInk : c.ink3,
          bgcolor: active ? c.tealTint : "transparent",
          fontWeight: active ? 600 : 500,
          fontSize: 13.5,
          "&:hover": { bgcolor: active ? c.tealTint : c.panelBg2 },
        }}
      >
        <Icon name={item.icon} size={19} color={active ? c.tealInk : c.muted} />
        <Typography component="span" sx={{ flex: 1, minWidth: 0, fontSize: "inherit", fontWeight: "inherit", color: "inherit" }}>
          {item.label}
        </Typography>
        {count > 0 && (
          <Box
            component="span"
            sx={{
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1,
              color: c.tealInk,
              bgcolor: c.tealTint,
              borderRadius: `${brainTokens.radius.pill}px`,
              px: 0.75,
              py: 0.35,
            }}
          >
            {count}
          </Box>
        )}
      </Box>
    </NavLink>
  );
}

export function MuiAppShell() {
  const { profile } = useProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [counts, setCounts] = useState({ review: 0, questions: 0 });
  const menuRef = useRef<HTMLDivElement>(null);

  // morning-brief badge — best-effort, swallow errors (identical to kt AppShell)
  useEffect(() => {
    let alive = true;
    callTool("morning_brief", { _format: "json" })
      .then((raw) => {
        const data = JSON.parse(raw);
        if (!alive) return;
        setCounts({
          review: data.pendingForReview?.length ?? 0,
          questions: typeof data.openQuestions === "number" ? data.openQuestions : 0,
        });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // outside-click + Esc close (identical to kt AppShell)
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const initials = useMemo(() => {
    const n = profile?.displayName?.trim();
    if (!n) return "I";
    const parts = n.split(/\s+/);
    return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || n.slice(0, 2).toUpperCase();
  }, [profile?.displayName]);

  const onLogout = () => {
    signOut();
    navigate("/login");
  };

  const visible = NAV.filter(
    (item) =>
      (!item.admin || profile?.role === "admin") &&
      (!item.finance || profile?.role === "admin" || profile?.departments.includes("finance")),
  );

  return (
    <ThemeProvider theme={brainTheme}>
      <CssBaseline />
      <AgentProvider>
        <CorrespondenceProvider>
        <NotificationsProvider>
        <SidebarSlotProvider>
          <Box sx={{ minHeight: "100vh", bgcolor: c.canvas, fontFamily: brainTokens.font.sans, color: c.ink }}>
            <AppBar
              position="fixed"
              elevation={0}
              sx={{ height: L.topBarHeight, bgcolor: c.topBar, justifyContent: "center", zIndex: (t) => t.zIndex.drawer + 1 }}
            >
              <Toolbar variant="dense" sx={{ minHeight: L.topBarHeight, justifyContent: "space-between" }}>
                {/* left: 3-dot cluster logo + "aeronet" wordmark + boxed BRAIN tag -> /today */}
                <IconButton
                  onClick={() => navigate("/today")}
                  aria-label="Aeronet Brain home"
                  disableRipple
                  sx={{
                    borderRadius: `${brainTokens.radius.button}px`,
                    display: "flex",
                    alignItems: "center",
                    gap: "11px",
                    px: 1,
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: 20,
                      height: 20,
                      flex: "none",
                      "& > span": { position: "absolute", width: 7.5, height: 7.5, borderRadius: "50%", display: "block" },
                      "& > span:nth-of-type(1)": { left: "6.25px", top: 0, bgcolor: c.brandTeal }, // apex: teal
                      "& > span:nth-of-type(2)": { left: 0, top: "12.5px", bgcolor: c.brandRed }, // bottom-left: red
                      "& > span:nth-of-type(3)": { left: "12.5px", top: "12.5px", bgcolor: c.brandOrange }, // bottom-right: orange
                    }}
                    aria-hidden="true"
                  >
                    <span />
                    <span />
                    <span />
                  </Box>
                  <Typography
                    component="span"
                    sx={{ color: c.teal, fontSize: 26, fontWeight: 400, letterSpacing: "0.13em", lineHeight: 1, fontFamily: brainTokens.font.sans }}
                  >
                    aeronet
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      alignSelf: "center",
                      lineHeight: 1,
                      fontFamily: brainTokens.font.mono,
                      fontSize: 12.5,
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                      color: alpha(c.topBarText, 0.82),
                      border: `1px solid ${alpha(c.topBarText, 0.28)}`,
                      borderRadius: "4px",
                      px: "8px",
                      py: "4px",
                    }}
                  >
                    brain
                  </Box>
                </IconButton>

                {/* right: notifications, help, logout, avatar */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <IconButton
                    aria-label="Raise at standup"
                    title="Raise at standup"
                    onClick={() => setRaiseOpen(true)}
                    sx={{ color: c.topBarText }}
                  >
                    <Icon name="plus" size={23} color={c.topBarText} />
                  </IconButton>
                  <NotificationBell />
                  <IconButton
                    aria-label="Help & setup guide"
                    title="Help & setup guide"
                    onClick={() => navigate("/guide")}
                    sx={{ color: c.topBarText }}
                  >
                    <Icon name="help" size={23} color={c.topBarText} />
                  </IconButton>
                  <IconButton aria-label="Log out" title="Log out" onClick={onLogout} sx={{ color: c.topBarText }}>
                    <Icon name="logout" size={23} color={c.topBarText} />
                  </IconButton>
                  <Box ref={menuRef}>
                    <IconButton
                      aria-label="Account menu"
                      aria-haspopup="menu"
                      aria-expanded={menuOpen}
                      onClick={() => setMenuOpen((v) => !v)}
                      onKeyDown={(e) => {
                        if (e.key === "ArrowDown") {
                          e.preventDefault();
                          setMenuOpen(true);
                        }
                      }}
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: `${brainTokens.radius.avatar}px`,
                        bgcolor: c.tealTint,
                        color: c.tealInk,
                        fontSize: 15,
                        fontWeight: 700,
                      }}
                    >
                      {initials}
                    </IconButton>
                    <Menu
                      open={menuOpen}
                      onClose={() => setMenuOpen(false)}
                      anchorEl={menuRef.current}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                      <MenuItem
                        onClick={() => {
                          setMenuOpen(false);
                          navigate("/settings");
                        }}
                      >
                        <ListItemIcon>
                          <Icon name="settings" size={16} />
                        </ListItemIcon>
                        <ListItemText>Settings</ListItemText>
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setMenuOpen(false);
                          onLogout();
                        }}
                      >
                        <ListItemIcon>
                          <Icon name="logout" size={16} />
                        </ListItemIcon>
                        <ListItemText>Log out</ListItemText>
                      </MenuItem>
                    </Menu>
                  </Box>
                </Box>
              </Toolbar>
            </AppBar>
            <Box sx={{ display: "flex", pt: `${L.topBarHeight}px` }}>
              <Box
                component="aside"
                sx={{
                  width: L.sidebarWidth,
                  flexShrink: 0,
                  bgcolor: c.sidebarBg,
                  borderRight: `1px solid ${c.border}`,
                  height: `calc(100vh - ${L.topBarHeight}px)`,
                  position: "sticky",
                  top: L.topBarHeight,
                  overflowY: "auto",
                }}
              >
                <Box component="nav" aria-label="Primary" sx={{ py: 1.5 }}>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      color: c.faint,
                      px: 2.5,
                      mb: 0.5,
                    }}
                  >
                    Navigate
                  </Typography>
                  {visible.map((item) => {
                    const active = location.pathname.startsWith(item.to);
                    const count = item.badge ? counts.review + counts.questions : 0;
                    return <NavRow key={item.to} item={item} active={active} count={count} />;
                  })}
                  {profile?.role === "admin" && (
                    <>
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          color: c.faint,
                          px: 2.5,
                          mt: 1.5,
                          mb: 0.5,
                        }}
                      >
                        Admin
                      </Typography>
                      {ADMIN_NAV.map((item) => {
                        const active = location.pathname.startsWith(item.to);
                        return <NavRow key={`admin-${item.to}`} item={item} active={active} count={0} />;
                      })}
                    </>
                  )}
                </Box>
                <Box sx={{ p: 1.5 }}>
                  <SidebarSlotOutlet />
                </Box>
              </Box>
              <Box
                component="main"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  width: "100%",
                  maxWidth: `${L.contentMaxWidth}px`,
                  // Left-aligned to the nav; surplus width stays blank on the right
                  mr: "auto",
                  bgcolor: c.canvas,
                  p: L.contentPadding,
                }}
              >
                <Outlet />
              </Box>
              {/* The agent docks as a real column here so the page reflows around it. */}
              <AgentWidget />
            </Box>
          </Box>
        </SidebarSlotProvider>
        <RaiseAtStandupDialog open={raiseOpen} onClose={() => setRaiseOpen(false)} />
        </NotificationsProvider>
        </CorrespondenceProvider>
      </AgentProvider>
    </ThemeProvider>
  );
}
